/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";
import {
  getArbitratorDecisionDecoder,
  getArbitratorDecisionEncoder,
  type ArbitratorDecision,
  type ArbitratorDecisionArgs,
} from "../types";

export const ARBITRATE_DISPUTE_DISCRIMINATOR = new Uint8Array([
  225, 89, 166, 101, 215, 40, 191, 4,
]);

export function getArbitrateDisputeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ARBITRATE_DISPUTE_DISCRIMINATOR,
  );
}

export type ArbitrateDisputeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountEscrow extends string | AccountMeta<string> = string,
  TAccountEscrowVault extends string | AccountMeta<string> = string,
  TAccountAgentTokenAccount extends string | AccountMeta<string> = string,
  TAccountClientTokenAccount extends string | AccountMeta<string> = string,
  TAccountAgentStaking extends string | AccountMeta<string> = string,
  TAccountArbitrator extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountEscrow extends string
        ? WritableAccount<TAccountEscrow>
        : TAccountEscrow,
      TAccountEscrowVault extends string
        ? WritableAccount<TAccountEscrowVault>
        : TAccountEscrowVault,
      TAccountAgentTokenAccount extends string
        ? WritableAccount<TAccountAgentTokenAccount>
        : TAccountAgentTokenAccount,
      TAccountClientTokenAccount extends string
        ? WritableAccount<TAccountClientTokenAccount>
        : TAccountClientTokenAccount,
      TAccountAgentStaking extends string
        ? WritableAccount<TAccountAgentStaking>
        : TAccountAgentStaking,
      TAccountArbitrator extends string
        ? ReadonlySignerAccount<TAccountArbitrator> &
            AccountSignerMeta<TAccountArbitrator>
        : TAccountArbitrator,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ArbitrateDisputeInstructionData = {
  discriminator: ReadonlyUint8Array;
  decision: ArbitratorDecision;
};

export type ArbitrateDisputeInstructionDataArgs = {
  decision: ArbitratorDecisionArgs;
};

export function getArbitrateDisputeInstructionDataEncoder(): Encoder<ArbitrateDisputeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["decision", getArbitratorDecisionEncoder()],
    ]),
    (value) => ({ ...value, discriminator: ARBITRATE_DISPUTE_DISCRIMINATOR }),
  );
}

export function getArbitrateDisputeInstructionDataDecoder(): Decoder<ArbitrateDisputeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["decision", getArbitratorDecisionDecoder()],
  ]);
}

export function getArbitrateDisputeInstructionDataCodec(): Codec<
  ArbitrateDisputeInstructionDataArgs,
  ArbitrateDisputeInstructionData
> {
  return combineCodec(
    getArbitrateDisputeInstructionDataEncoder(),
    getArbitrateDisputeInstructionDataDecoder(),
  );
}

export type ArbitrateDisputeInput<
  TAccountEscrow extends string = string,
  TAccountEscrowVault extends string = string,
  TAccountAgentTokenAccount extends string = string,
  TAccountClientTokenAccount extends string = string,
  TAccountAgentStaking extends string = string,
  TAccountArbitrator extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  escrow: Address<TAccountEscrow>;
  escrowVault: Address<TAccountEscrowVault>;
  agentTokenAccount: Address<TAccountAgentTokenAccount>;
  clientTokenAccount: Address<TAccountClientTokenAccount>;
  /** Agent's staking account (for potential slashing) */
  agentStaking: Address<TAccountAgentStaking>;
  arbitrator: TransactionSigner<TAccountArbitrator>;
  tokenProgram?: Address<TAccountTokenProgram>;
  decision: ArbitrateDisputeInstructionDataArgs["decision"];
};

export function getArbitrateDisputeInstruction<
  TAccountEscrow extends string,
  TAccountEscrowVault extends string,
  TAccountAgentTokenAccount extends string,
  TAccountClientTokenAccount extends string,
  TAccountAgentStaking extends string,
  TAccountArbitrator extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ArbitrateDisputeInput<
    TAccountEscrow,
    TAccountEscrowVault,
    TAccountAgentTokenAccount,
    TAccountClientTokenAccount,
    TAccountAgentStaking,
    TAccountArbitrator,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ArbitrateDisputeInstruction<
  TProgramAddress,
  TAccountEscrow,
  TAccountEscrowVault,
  TAccountAgentTokenAccount,
  TAccountClientTokenAccount,
  TAccountAgentStaking,
  TAccountArbitrator,
  TAccountTokenProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    escrowVault: { value: input.escrowVault ?? null, isWritable: true },
    agentTokenAccount: {
      value: input.agentTokenAccount ?? null,
      isWritable: true,
    },
    clientTokenAccount: {
      value: input.clientTokenAccount ?? null,
      isWritable: true,
    },
    agentStaking: { value: input.agentStaking ?? null, isWritable: true },
    arbitrator: { value: input.arbitrator ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.escrowVault),
      getAccountMeta(accounts.agentTokenAccount),
      getAccountMeta(accounts.clientTokenAccount),
      getAccountMeta(accounts.agentStaking),
      getAccountMeta(accounts.arbitrator),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getArbitrateDisputeInstructionDataEncoder().encode(
      args as ArbitrateDisputeInstructionDataArgs,
    ),
    programAddress,
  } as ArbitrateDisputeInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountEscrowVault,
    TAccountAgentTokenAccount,
    TAccountClientTokenAccount,
    TAccountAgentStaking,
    TAccountArbitrator,
    TAccountTokenProgram
  >);
}

export type ParsedArbitrateDisputeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    escrow: TAccountMetas[0];
    escrowVault: TAccountMetas[1];
    agentTokenAccount: TAccountMetas[2];
    clientTokenAccount: TAccountMetas[3];
    /** Agent's staking account (for potential slashing) */
    agentStaking: TAccountMetas[4];
    arbitrator: TAccountMetas[5];
    tokenProgram: TAccountMetas[6];
  };
  data: ArbitrateDisputeInstructionData;
};

export function parseArbitrateDisputeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedArbitrateDisputeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 7) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      escrow: getNextAccount(),
      escrowVault: getNextAccount(),
      agentTokenAccount: getNextAccount(),
      clientTokenAccount: getNextAccount(),
      agentStaking: getNextAccount(),
      arbitrator: getNextAccount(),
      tokenProgram: getNextAccount(),
    },
    data: getArbitrateDisputeInstructionDataDecoder().decode(instruction.data),
  };
}
